import { Button } from '@/components';
import whatsapp_icon from '../../assets/whatsapp.png';
import { AUTH_BUTTON_CLASS } from '../../lib/authStyles';

const WHATSAPP_DESCRIPTION =
  'Connect your WhatsApp Business account to send automated messages, manage customer conversations and track engagement - all from one dashboard.';

export function ChooseIntegrationForm({
  onSelect,
  integrations,
  isLoading,
  error,
}) {
  const availableIntegrations = (
    Array.isArray(integrations)
      ? integrations
      : Array.isArray(integrations?.results)
        ? integrations.results
        : []
  ).filter((integration) =>
    String(integration.slug || integration.name || '')
      .toLowerCase()
      .includes('whatsapp'),
  );

  const comingSoonList = [
    {
      name: 'Telegram',
      icon: 'T',
      color: 'bg-[#0088cc]/20 text-[#0088cc]',
      date: 'coming Q3 2026',
    },
    {
      name: 'Instagram',
      icon: 'I',
      color: 'bg-[#e1306c]/20 text-[#e1306c]',
      date: 'coming Q3 2026',
    },
    {
      name: 'Slack',
      icon: 'S',
      color: 'bg-[#4a154b]/20 text-[#e01e5a]',
      date: 'coming Q3 2026',
    },
    {
      name: 'Shopify',
      icon: 'S',
      color: 'bg-[#96bf48]/20 text-[#96bf48]',
      date: 'coming Q3 2026',
    },
    {
      name: 'Salesforce',
      icon: 'S',
      color: 'bg-[#00a1e0]/20 text-[#00a1e0]',
      date: 'coming Q3 2026',
    },
    {
      name: 'HubSpot',
      icon: 'H',
      color: 'bg-[#ff7a59]/20 text-[#ff7a59]',
      date: 'coming Q3 2026',
    },
  ];

  return (
    <div className="w-full flex flex-col gap-6 text-left">
      {/* Available Now */}
      <div className="flex flex-col gap-3">
        <span className="text-h3-medium text-text-primary font-sf-pro-display">
          Available Now
        </span>

        {isLoading ? (
          <div className="w-full h-40 bg-input-background border border-border-default rounded-card flex items-center justify-center">
            <svg
              className="animate-spin h-8 w-8 text-primary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : error ? (
          <div className="w-full p-6 bg-red-500/10 border border-red-500/20 rounded-card text-red-500 font-sf-pro-display text-body text-center">
            Failed to load integrations. Please try again.
          </div>
        ) : availableIntegrations && availableIntegrations.length > 0 ? (
          availableIntegrations.map((integration) => {
            return (
              <div
                key={integration.id}
                className="w-full bg-input-background border border-primary/30 rounded-card p-4 md:p-6 flex flex-col sm:flex-row gap-5 items-start transition-all relative overflow-hidden group"
              >
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Integration Logo */}
                <div className="w-12 h-12">
                  <img
                    src={integration.icon_url || whatsapp_icon}
                    alt={integration.name}
                  />
                </div>

                {/* Details */}
                <div className="flex flex-col gap-3 w-full z-10">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-h3-medium text-primary font-sf-pro-display">
                      {integration.name}
                    </h3>
                    <p className="text-h4-normal !leading-snug text-text-muted font-sf-pro-display">
                      {WHATSAPP_DESCRIPTION}
                    </p>
                  </div>

                  <Button
                    size="lg"
                    type="button"
                    variant="primary"
                    className={`${AUTH_BUTTON_CLASS} w-full sm:w-[250px] h-[48px]`}
                    onClick={() => onSelect(integration.slug)}
                  >
                    Select & Configure →
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="w-full p-6 bg-input-background border border-border-default rounded-card text-text-muted font-sf-pro-display text-body text-center">
            No integrations currently available.
          </div>
        )}
      </div>

      {/* Coming Soon */}
      <div className="flex flex-col gap-3.5 mt-1">
        <span className="text-overline text-text-muted tracking-widest text-body sm:text-h6 uppercase">
          Coming Soon
        </span>

        {/* Integration Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-3`">
          {comingSoonList.map((item, idx) => (
            <div
              key={idx}
              className="bg-white/[0.01] border border-border-default rounded-card p-3 flex gap-3 items-center opacity-40 hover:opacity-50 transition-opacity duration-300"
            >
              {/* Dummy logo representing integration initials */}
              <div
                className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center font-bold text-sm font-sf-pro-display shrink-0`}
              >
                {item.icon}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-body sm:text-body-xl text-text-primary truncate font-sf-pro-display">
                  {item.name}
                </span>
                <span
                  className="text-body-sm text-text-secondary font-sf-pro-display"
                  title={item.date}
                >
                  {item.date}
                </span>
              </div>
            </div>
          ))}

          {/* Request Integration Card */}
          <div className="border border-dashed border-white/10 hover:border-border-default rounded-card p-3 flex gap-3 items-center cursor-pointer group transition-colors duration-300">
            <div className="w-9 h-9 rounded-lg bg-white/5 group-hover:bg-white/10 flex items-center justify-center text-text-muted group-hover:text-text-primary/80 shrink-0 transition-colors duration-300">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-body sm:text-body-xl text-text-muted group-hover:text-text-primary/80 truncate font-sf-pro-display transition-colors duration-300">
                Request
              </span>
              <span className="text-xxs sm:text-body-sm text-text-muted group-hover:text-text-primary/40 font-sf-pro-display transition-colors duration-300">
                Integration
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
